import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	MenuItem,
	Paper,
	TextField,
	Typography,
} from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import SummarizeOutlinedIcon from "@mui/icons-material/SummarizeOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import HistoryEduIcon from "@mui/icons-material/HistoryEdu";
import AppRouterConfig from "../../../AppRouterConfig";

const API = process.env.REACT_APP_LAMBDA_ARTEH_API_DOMAIN;

/** AASB S2 metrics & targets disclosure + board summary downloads. */
const openDownload = (url: string) => {
	// window.open after an async wait gets popup-blocked (the PDF takes
	// seconds to render), so navigate an anchor instead: the presigned URL
	// carries Content-Disposition attachment and downloads in place.
	const link = document.createElement("a");
	link.href = url;
	link.rel = "noopener";
	document.body.appendChild(link);
	link.click();
	link.remove();
};

const S2Report: React.FC = () => {
	const { getAccessTokenSilently } = useAuth0();
	const currentYear = new Date().getFullYear();
	const [year, setYear] = useState<number>(currentYear - 1);
	const [busy, setBusy] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);

	const downloadPdf = async (board: boolean) => {
		const key = board ? "board-pdf" : "s2-pdf";
		try {
			setBusy(key);
			setError(null);
			const token = await getAccessTokenSilently();
			const res = await fetch(
				`${API}/api/s2-report/pdf?year=${year}${board ? "&format=board" : ""}`,
				{ headers: { Authorization: `Bearer ${token}` } },
			);
			const json = await res.json().catch(() => ({}));
			if (!res.ok || !json.Ok || !json.Data?.url)
				throw new Error(json.Message || "Could not generate the PDF");
			openDownload(json.Data.url);
		} catch (e: any) {
			setError(e.message || "Could not generate the PDF");
		} finally {
			setBusy(null);
		}
	};

	const download = async (board: boolean) => {
		setBusy(board ? "board" : "s2");
		setError(null);
		try {
			const token = await getAccessTokenSilently();
			const res = await fetch(
				`${API}/api/s2-report?year=${year}${board ? "&format=board" : ""}`,
				{ headers: { Authorization: `Bearer ${token}` } },
			);
			if (!res.ok) {
				const data = await res.json().catch(() => ({}));
				setError(
					data.Message ||
						"Could not generate the document. Try again.",
				);
				return;
			}
			const blob = await res.blob();
			const disposition = res.headers.get("Content-Disposition") || "";
			const match = disposition.match(/filename="?([^";]+)"?/);
			const filename =
				match?.[1] ||
				(board
					? `board_summary_${year}.html`
					: `s2_disclosure_${year}.html`);
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.download = filename;
			document.body.appendChild(link);
			link.click();
			link.remove();
			window.URL.revokeObjectURL(url);
		} catch {
			setError("Could not generate the document. Check your connection.");
		} finally {
			setBusy(null);
		}
	};

	const years = Array.from({ length: 6 }, (_, i) => currentYear - i);

	return (
		<Box display="flex" justifyContent="center" padding={3}>
			<Paper
				elevation={0}
				sx={{
					maxWidth: 620,
					width: "100%",
					padding: "32px",
					border: "1px solid rgba(0, 23, 28, 0.10)",
					borderRadius: "12px",
				}}
			>
				<Typography variant="h5" gutterBottom>
					AASB S2 report
				</Typography>
				<Typography
					variant="body2"
					color="text.secondary"
					sx={{ marginBottom: 3 }}
				>
					Generate the metrics-and-targets disclosure section for a
					reporting year: gross Scope 1, 2 (location- and
					market-based) and 3 emissions, the 15-category Scope 3
					table, base-year comparatives from your locked baselines,
					restatements, and the measurement methodology appendix.
					Download as a branded PDF, or as printable HTML.
				</Typography>

				<Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
					<TextField
						select
						label="Reporting year"
						value={year}
						onChange={(e) => setYear(Number(e.target.value))}
						size="small"
						sx={{ minWidth: 150 }}
					>
						{years.map((y) => (
							<MenuItem key={y} value={y}>
								{y}
							</MenuItem>
						))}
					</TextField>
					<Button
						variant="contained"
						color="secondary"
						startIcon={
							busy === "s2" ? (
								<CircularProgress size={16} color="inherit" />
							) : (
								<DescriptionOutlinedIcon />
							)
						}
						disabled={busy !== null}
						onClick={() => download(false)}
					>
						{busy === "s2" ? "Generating…" : "S2 disclosure"}
					</Button>
					<Button
						variant="outlined"
						color="secondary"
						startIcon={
							busy === "board" ? (
								<CircularProgress size={16} color="inherit" />
							) : (
								<SummarizeOutlinedIcon />
							)
						}
						disabled={busy !== null}
						onClick={() => download(true)}
					>
						{busy === "board" ? "Generating…" : "Board summary"}
					</Button>
					<Button
						variant="outlined"
						color="secondary"
						startIcon={
							busy === "s2-pdf" ? (
								<CircularProgress size="1rem" />
							) : (
								<PictureAsPdfOutlinedIcon />
							)
						}
						disabled={busy !== null}
						onClick={() => downloadPdf(false)}
					>
						{busy === "s2-pdf" ? "Rendering…" : "S2 disclosure (PDF)"}
					</Button>
					<Button
						variant="outlined"
						color="secondary"
						startIcon={
							busy === "board-pdf" ? (
								<CircularProgress size="1rem" />
							) : (
								<PictureAsPdfOutlinedIcon />
							)
						}
						disabled={busy !== null}
						onClick={() => downloadPdf(true)}
					>
						{busy === "board-pdf" ? "Rendering…" : "Board summary (PDF)"}
					</Button>
				</Box>

				{error && (
					<Alert severity="info" sx={{ marginTop: 3 }}>
						{error}
					</Alert>
				)}

				<Typography
					variant="body2"
					color="text.secondary"
					sx={{ marginTop: 3 }}
				>
					The Governance, Strategy and Risk management sections render
					from your authored narratives — sections without one show a
					guided placeholder.
				</Typography>
				<Button
					size="small"
					startIcon={<HistoryEduIcon />}
					component={Link}
					to={AppRouterConfig.USERCOMPANY.REPORT.DISCLOSURE_WORKSPACE}
					sx={{ marginTop: 1 }}
				>
					Edit narratives in the Disclosure workspace
				</Button>
			</Paper>
		</Box>
	);
};

export default S2Report;
